import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Coins,
  Gift,
  Backpack,
  Pencil,
  Check,
  Trash2,
  Info,
  X,
  Store,
  TrendingUp,
  WifiOff,
  Brush,
  Lock,
  Move,
  RotateCcw,
  GraduationCap,
  Share2,
  FlipHorizontal2,
  Check as CheckIcon,
  ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import IsoIsland from "./oasis/IsoIsland";
import { GROUND_TYPES } from "./oasis/tileset";
import AdminPanel from "./oasis/AdminPanel";
import Sprite from "./oasis/Sprite";
import { useAuth } from "@/context/AuthContext";
import {
  ConfirmPurchase,
  BoxOpening,
  ChunkCelebration,
} from "./oasis/Feedback";
import Tutorial from "./oasis/Tutorial";
import { SETS, SET_BY_ID, pendingRewards } from "./oasis/sets";
import {
  CATALOG,
  BY_ID,
  CATEGORIES,
  categoryLabel,
  isShopItem,
  isNpc,
  poolFor,
} from "./oasis/catalog";
import { loadCustomItems } from "./oasis/customItems";
import {
  EARN,
  RARITIES,
  RARITY_ORDER,
  BOXES,
  PITY_THRESHOLD,
  STARTING_CHUNKS,
  MAX_CHUNKS,
  CHUNK_TIERS,
  npcPriceFor,
  npcPriceTotal,
  chunkPrice,
  rollRarity,
  resaleValue,
  priceOf,
  tierFor,
  fmt,
} from "./oasis/economy";
import { t } from "@/i18n";

const uid = () => Math.random().toString(36).slice(2, 10);

const coversTile = (p, tx, ty) => {
  const [w, d] = BY_ID[p.itemId]?.size || [1, 1];
  return tx >= p.x && tx < p.x + w && ty >= p.y && ty < p.y + d;
};

function RarityChip({ rarity, className = "" }) {
  const r = RARITIES[rarity] || RARITIES.commun;
  return (
    <span
      className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wide px-1.5 sm:px-2 py-0.5 rounded-full ${r.chip} ${className}`}
    >
      {r.label}
    </span>
  );
}

export default function Rewards() {
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [tab, setTab] = useState("boutique");
  const [balance, setBalance] = useState(0);
  const [inventory, setInventory] = useState({});
  const [island, setIsland] = useState({
    chunks: STARTING_CHUNKS,
    placed: [],
    terrain: {},
  });
  const [pity, setPity] = useState(0);

  const [editMode, setEditMode] = useState(null);
  const [paintGround, setPaintGround] = useState("dirt");
  const [pending, setPending] = useState(null);
  const [pendingFlip, setPendingFlip] = useState(false);
  const [movingUid, setMovingUid] = useState(null);
  const [menuAt, setMenuAt] = useState(null);
  const [selectedPlaced, setSelectedPlaced] = useState(null);
  const [toast, setToast] = useState(null);
  const [help, setHelp] = useState(false);
  const [setReward, setSetReward] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [opening, setOpening] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [calibrate, setCalibrate] = useState(false);
  const [adminSky, setAdminSky] = useState({ phase: null, rain: null });
  const [imgVersion, setImgVersion] = useState(0);
  const [catalogVersion, setCatalogVersion] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [tourActive, setTourActive] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!localStorage.getItem("oasis_tour_seen")) {
      const timer = setTimeout(() => setTourActive(true), 700);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const closeTour = () => {
    localStorage.setItem("oasis_tour_seen", "1");
    setTourActive(false);
  };

  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const openShare = () => {
    setShareOpen(true);
    if (!shareLink) {
      api
        .get("/oasis/share-link")
        .then((r) =>
          setShareLink(`${window.location.origin}/oasis/s/${r.data.token}`),
        )
        .catch(() => setShareLink(null));
    }
  };

  const copyShare = () => {
    if (!shareLink) return;
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const { user: authUser, updateDinars } = useAuth();
  const admin = !!authUser && authUser.role === "admin";

  const applyServerBalance = useCallback(
    (nextBalance) => {
      if (typeof nextBalance !== "number") return;
      setBalance(nextBalance);
      updateDinars(nextBalance);
      window.dispatchEvent(
        new CustomEvent("arabdex:dinar-update", {
          detail: { dinars_balance: nextBalance },
        }),
      );
    },
    [updateDinars],
  );

  useEffect(() => {
    const onDinarUpdate = (event) => {
      const nextBalance = event.detail?.dinars_balance ?? event.detail?.balance;
      if (typeof nextBalance === "number") {
        setBalance(nextBalance);
        updateDinars(nextBalance);
      }
    };
    window.addEventListener("arabdex:dinar-update", onDinarUpdate);
    return () =>
      window.removeEventListener("arabdex:dinar-update", onDinarUpdate);
  }, [updateDinars]);

  const say = useCallback((msg) => {
    setToast(msg);
    setTimeout(
      () =>
        setToast((currentToast) =>
          currentToast === msg ? null : currentToast,
        ),
      2600,
    );
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/recompenses"),
      api.get("/oasis").catch(() => null),
      loadCustomItems().catch(() => null),
    ])
      .then(([rewards, oasis]) => {
        if (!alive) return;
        setCatalogVersion((v) => v + 1);
        applyServerBalance(rewards.data.balance ?? 0);
        if (oasis?.data) {
          setInventory(oasis.data.inventory ?? {});
          setIsland(
            oasis.data.island?.chunks?.length
              ? { terrain: {}, ...oasis.data.island }
              : { chunks: STARTING_CHUNKS, placed: [], terrain: {} },
          );
          setPity(oasis.data.pity ?? 0);
        } else setOffline(true);
      })
      .catch(() => {
        if (alive) {
          setOffline(true);
          setBalance(0);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [applyServerBalance]);

  const saveLayout = useCallback((next) => {
    api
      .put("/oasis/plan", {
        placed: next.placed,
        chunks: next.chunks,
        terrain: next.terrain,
      })
      .catch(() => setOffline(true));
  }, []);

  const placedCount = useMemo(() => {
    const m = {};
    for (const p of island.placed) m[p.itemId] = (m[p.itemId] || 0) + 1;
    return m;
  }, [island.placed]);

  const available = useCallback(
    (itemId) => (inventory[itemId] || 0) - (placedCount[itemId] || 0),
    [inventory, placedCount],
  );

  const ownedList = useMemo(
    () => CATALOG.filter((i) => (inventory[i.id] || 0) > 0),
    [inventory, catalogVersion],
  );
  const nextChunkCost = chunkPrice(island.chunks.length);

  const spend = (amount) => {
    setBalance((b) => b - amount);
  };

  const buyChunk = (cell) => {
    if (nextChunkCost === null)
      return say(t("rewards.archipel_complet_bravo", { max: MAX_CHUNKS }));
    setConfirm({
      kind: "chunk",
      title: t("rewards.etendre_l_archipel"),
      subtitle: t("rewards.un_pont_se_construira"),
      price: nextChunkCost,
      cta: t("rewards.debloquer"),
      run: () => {
        setIsland((is) => ({ ...is, chunks: [...is.chunks, cell] }));
        spend(nextChunkCost);
        setCelebrate(true);
        api
          .post("/oasis/parcelle", cell)
          .then((r) => {
            if (r.data?.balance != null) applyServerBalance(r.data.balance);
          })
          .catch(() => setOffline(true));
      },
    });
  };

  const buyItem = (item) => {
    const cost = priceOf(item);
    setConfirm({
      kind: "item",
      title: item.name,
      subtitle: item.desc,
      price: cost,
      item,
      allowQuantity: true,
      run: (qty = 1) => {
        setInventory((inv) => ({
          ...inv,
          [item.id]: (inv[item.id] || 0) + qty,
        }));
        spend(cost * qty);
        api
          .post("/oasis/acheter", { itemId: item.id, qty })
          .then((r) => {
            if (r.data?.balance != null) applyServerBalance(r.data.balance);
          })
          .catch(() => setOffline(true));
        say(
          qty > 1
            ? t("rewards.objets_rejoignent_sac", { name: item.name, qty })
            : t("rewards.objet_rejoint_sac", { name: item.name }),
        );
      },
    });
  };

  const buyNpc = (npcItem) => {
    const owned = inventory[npcItem.id] || 1;
    const next = npcPriceFor(owned);
    setConfirm({
      kind: "npc",
      title: npcItem.name,
      subtitle: t("rewards.un_habitant_de_plus", {
        price: fmt(npcPriceFor(owned + 1)),
      }),
      price: next,
      item: npcItem,
      allowQuantity: true,
      totalFor: (q) => npcPriceTotal(owned, q),
      cta: t("rewards.faire_venir"),
      run: (qty = 1) => {
        const cost = npcPriceTotal(owned, qty);
        setInventory((inv) => ({
          ...inv,
          [npcItem.id]: (inv[npcItem.id] || 0) + qty,
        }));
        spend(cost);
        api
          .post("/oasis/acheter", { itemId: npcItem.id, qty })
          .then((r) => {
            if (r.data?.balance != null) applyServerBalance(r.data.balance);
          })
          .catch(() => setOffline(true));
        say(
          qty > 1
            ? t("rewards.habitants_emmenagent", { name: npcItem.name, qty })
            : t("rewards.habitant_emmenage", { name: npcItem.name }),
        );
      },
    });
  };

  const sellItem = (item) => {
    if (available(item.id) <= 0)
      return say(t("rewards.decor_pose_retirer_dabord"));
    const gain = resaleValue(item);
    setInventory((inv) => {
      const n = { ...inv, [item.id]: inv[item.id] - 1 };
      if (n[item.id] <= 0) delete n[item.id];
      return n;
    });
    setBalance((b) => b + gain);
    api
      .post("/oasis/revendre", { itemId: item.id })
      .then((r) => {
        if (r.data?.balance != null) applyServerBalance(r.data.balance);
      })
      .catch(() => setOffline(true));
  };

  const reallyOpenBox = (box) => {
    spend(box.price);
    const rarity = rollRarity(box, pity);
    const pool = poolFor(rarity);
    const item = pool[Math.floor(Math.random() * pool.length)];
    if (!item) return;
    const isNew = !(inventory[item.id] > 0);
    setInventory((inv) => ({ ...inv, [item.id]: (inv[item.id] || 0) + 1 }));
    setPity((p) => (RARITIES[rarity].order >= 3 ? 0 : p + 1));
    setOpening({ box, result: { item, rarity, isNew } });

    api
      .post("/oasis/coffre", { boxId: box.id })
      .then((r) => {
        if (!r.data) return;
        if (r.data.balance != null) applyServerBalance(r.data.balance);
        if (r.data.pity != null) setPity(r.data.pity);
        if (r.data.itemId && r.data.itemId !== item.id) {
          setInventory((inv) => {
            const n = { ...inv, [item.id]: (inv[item.id] || 1) - 1 };
            if (n[item.id] <= 0) delete n[item.id];
            n[r.data.itemId] = (n[r.data.itemId] || 0) + 1;
            return n;
          });
          setOpening({
            box,
            result: {
              item: BY_ID[r.data.itemId],
              rarity: r.data.rarity,
              isNew: r.data.isNew,
            },
          });
        }
      })
      .catch(() => setOffline(true));
  };

  const openBox = (box) => {
    if (box.price > 0) {
      setConfirm({
        kind: "box",
        title: box.name,
        subtitle: box.tag,
        price: box.price,
        cta: t("rewards.ouvrir"),
        run: () => reallyOpenBox(box),
      });
      return;
    }
    reallyOpenBox(box);
  };

  const placeAt = (tx, ty) => {
    if (!pending) return;
    if (movingUid) {
      const next = {
        ...island,
        placed: island.placed.map((p) =>
          p.uid === movingUid ? { ...p, x: tx, y: ty, flip: pendingFlip } : p,
        ),
      };
      setIsland(next);
      saveLayout(next);
      setMovingUid(null);
      setPending(null);
      setPendingFlip(false);
      return;
    }
    const next = {
      ...island,
      placed: [
        ...island.placed,
        { uid: uid(), itemId: pending.id, x: tx, y: ty, flip: pendingFlip },
      ],
    };
    setIsland(next);
    saveLayout(next);
    if (available(pending.id) - 1 <= 0) {
      setPending(null);
      setPendingFlip(false);
    }
  };

  const resetIsland = () => {
    const next = { ...island, placed: [], terrain: {} };
    setIsland(next);
    saveLayout(next);
    setSelectedPlaced(null);
    setMenuAt(null);
    setPending(null);
    setMovingUid(null);
    say(t("rewards.ile_redevenue_vierge"));
  };

  const dropItem = (itemId, tx, ty) => {
    const item = BY_ID[itemId];
    if (!item || available(itemId) <= 0) return;
    const next = {
      ...island,
      placed: [
        ...island.placed,
        { uid: uid(), itemId, x: tx, y: ty, flip: pendingFlip },
      ],
    };
    setIsland(next);
    saveLayout(next);
    setPending(null);
    setPendingFlip(false);
  };

  const locateCursor = useRef({});
  const locateNext = (itemId) => {
    const list = island.placed.filter((p) => p.itemId === itemId);
    if (!list.length) return;
    const n = (locateCursor.current[itemId] ?? -1) + 1;
    locateCursor.current[itemId] = n % list.length;
    const target = list[n % list.length];
    setSelectedPlaced(target.uid);
    setPending(null);
    setMenuAt(
      (m) => m || { x: window.innerWidth / 2, y: window.innerHeight * 0.55 },
    );
    say(
      list.length > 1
        ? `${BY_ID[itemId].name} ${(n % list.length) + 1}/${list.length}`
        : `${BY_ID[itemId].name}`,
    );
  };

  const startMoving = (placed) => {
    const item = BY_ID[placed.itemId];
    if (!item) return;
    setPending(item);
    setPendingFlip(!!placed.flip);
    setMovingUid(placed.uid);
    setSelectedPlaced(null);
  };

  const flipPlaced = (id) => {
    const next = {
      ...island,
      placed: island.placed.map((p) =>
        p.uid === id ? { ...p, flip: !p.flip } : p,
      ),
    };
    setIsland(next);
    saveLayout(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "r" && e.key !== "R") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (pending) {
        setPendingFlip((f) => !f);
        e.preventDefault();
        return;
      }
      if (selectedPlaced) {
        flipPlaced(selectedPlaced);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    const due = pendingRewards(inventory);
    if (!due.length) return;
    const set = due[0];
    setInventory((inv) => ({
      ...inv,
      [set.npc.id]: (inv[set.npc.id] || 0) + 1,
    }));
    setSetReward(set);
    api
      .post("/oasis/confrerie", { setId: set.id })
      .catch(() => setOffline(true));
  }, [inventory]);

  const grantSet = (setId) => {
    const set = SET_BY_ID[setId];
    if (!set) return;
    setInventory((inv) => {
      const next = { ...inv };
      for (const bld of set.buildings) next[bld.id] = (next[bld.id] || 0) + 1;
      return next;
    });
  };

  const paintAt = (tx, ty, type) => {
    const withoutGround = island.placed.filter(
      (p) => !(BY_ID[p.itemId]?.layer === "sol" && coversTile(p, tx, ty)),
    );

    if (typeof type === "string" && type.startsWith("item:")) {
      const itemId = type.slice(5);
      const already = island.placed.find(
        (p) =>
          p.itemId === itemId &&
          BY_ID[itemId]?.layer === "sol" &&
          coversTile(p, tx, ty),
      );
      if (already) return;
      const freed = island.placed.length - withoutGround.length;
      if (available(itemId) + (freed ? 0 : 0) <= 0)
        return say(t("rewards.plus_de_bloc_en_stock"));
      const next = {
        ...island,
        placed: [
          ...withoutGround,
          { uid: uid(), itemId, x: tx, y: ty, flip: false },
        ],
      };
      setIsland(next);
      saveLayout(next);
      return;
    }

    const next = {
      ...island,
      placed: withoutGround,
      terrain: { ...island.terrain, [`${tx},${ty}`]: type },
    };
    setIsland(next);
    saveLayout(next);
  };

  const removePlaced = (id) => {
    const next = {
      ...island,
      placed: island.placed.filter((p) => p.uid !== id),
    };
    setIsland(next);
    saveLayout(next);
    setSelectedPlaced(null);
    say(t("rewards.decor_renvoye_sac"));
  };

  const startPlacing = (item) => {
    setPending(item);
    setPendingFlip(false);
    setMovingUid(null);
    setEditMode("decor");
    setSelectedPlaced(null);
  };

  const toggleEdit = () => {
    setEditMode((m) => (m ? null : "decor"));
    setPending(null);
    setPendingFlip(false);
    setMovingUid(null);
    setSelectedPlaced(null);
    setMenuAt(null);
  };

  const exitEditor = () => {
    saveLayout(island);
    setEditMode(null);
    setPending(null);
    setPendingFlip(false);
    setMovingUid(null);
    setSelectedPlaced(null);
    setMenuAt(null);
    say(t("rewards.amenagement_enregistre"));
  };

  if (loading)
    return (
      <div className="p-8 text-center text-muted-foreground animate-pulse">
        {t("rewards.chargement_de_ton_oasis")}
      </div>
    );

  const selected = selectedPlaced
    ? island.placed.find((p) => p.uid === selectedPlaced)
    : null;
  const selectedItem = selected ? BY_ID[selected.itemId] : null;

  const TABS = [
    { id: "boutique", label: t("rewards.onglet_boutique"), icon: Store },
    { id: "coffres", label: t("rewards.onglet_coffres"), icon: Gift },
    {
      id: "sac",
      label: t("rewards.onglet_sac"),
      icon: Backpack,
      badge: ownedList.length,
    },
  ];

  const decorPalette = (
    <DecorPalette
      ownedList={ownedList}
      available={available}
      placedCount={placedCount}
      pending={pending}
      onPick={(item) => {
        if (available(item.id) > 0) {
          setPending(pending?.id === item.id ? null : item);
          setPendingFlip(false);
        } else locateNext(item.id);
      }}
      onDragStartItem={(item) => setPending(item)}
      onDragEndItem={() => setPending(null)}
    />
  );

  const terrainPalette = (
    <TerrainPalette
      ownedList={ownedList}
      available={available}
      paintGround={paintGround}
      onPick={setPaintGround}
    />
  );

  const overlay =
    fullscreen && editMode ? (
      <div className="absolute inset-x-0 bottom-0 z-[30] p-3 pointer-events-none">
        <div className="pointer-events-auto rounded-3xl border bg-card/95 backdrop-blur shadow-2xl p-3 max-h-[42vh] overflow-y-auto">
          <div className="flex gap-1.5 mb-2">
            {[
              ["decor", t("rewards.objets"), Pencil],
              ["terrain", t("rewards.sol"), Brush],
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setEditMode(id);
                  setPending(null);
                  setSelectedPlaced(null);
                  setMenuAt(null);
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                  editMode === id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-muted/70"
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
            <button
              type="button"
              onClick={exitEditor}
              className="rounded-xl px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white"
            >
              {t("dailyplansession.terminer")}
            </button>
          </div>
          {editMode === "decor" ? decorPalette : terrainPalette}
        </div>
      </div>
    ) : null;

  return (
    <div className="w-full pb-8">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 space-y-2 sm:space-y-4">
        {/* EN-TÊTE ULTRA COMPACT POUR MOBILE */}
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-primary">
                {t("rewards.recompenses")}
              </span>
              <h1 className="font-head text-xl sm:text-3xl font-extrabold leading-none">
                {t("rewards.ton_oasis")}
              </h1>
            </div>
            <p className="hidden sm:block text-muted-foreground mt-0.5 text-xs sm:text-sm">
              {t("rewards.apprends_l_arabe_gagne_des_dinars")}
            </p>
          </div>
          <div className="shrink-0 flex gap-1">
            <button
              type="button"
              onClick={() => setTourActive(true)}
              title={t("rewards.tutoriel")}
              data-tour="help-btn"
              className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs hover:bg-muted transition"
            >
              <GraduationCap size={14} />
              <span className="hidden sm:inline">{t("rewards.tutoriel")}</span>
            </button>
            <button
              type="button"
              onClick={() => setHelp(true)}
              title={t("rewards.comment_ca_marche")}
              className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs hover:bg-muted transition"
            >
              <Info size={14} />
              <span className="hidden sm:inline">
                {t("rewards.comment_ca_marche")}
              </span>
            </button>
            <button
              type="button"
              onClick={openShare}
              title={t("rewards.partager")}
              className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs hover:bg-muted transition"
            >
              <Share2 size={14} />
              <span className="hidden sm:inline">{t("rewards.partager")}</span>
            </button>
          </div>
        </div>

        {offline && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300">
            <WifiOff size={14} className="shrink-0" />
            {t("rewards.oasis_non_synchronisee")}
          </div>
        )}

        {/* BANDEAU BALANCE COMPACT */}
        <div className="flex items-center justify-between rounded-xl sm:rounded-2xl border bg-card px-3 py-1.5 sm:py-2 text-xs">
          <div className="flex items-center gap-1.5 shrink-0">
            <Coins size={16} className="text-amber-500" />
            <span className="font-head text-base sm:text-lg font-extrabold tabular-nums">
              {fmt(balance)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {t("dinarcelebration.dinars")}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
            <span>
              {tierFor(island.chunks.length).label} · {island.chunks.length}/
              {MAX_CHUNKS}
            </span>
            <span className="hidden sm:inline">
              · {ownedList.length}/{CATALOG.length} {t("rewards.pieces")}
            </span>
          </div>
        </div>
      </div>

      {/* ZONE ÎLE - HAUTEUR RÉDUITE SUR MOBILE */}
      <div className="max-w-[1600px] mx-auto sm:px-4 mt-2 sm:mt-4">
        <section
          data-tour="island"
          className="border-y sm:border sm:rounded-3xl bg-card overflow-hidden flex flex-col"
        >
          <div className="px-3 py-2 sm:p-4 border-b flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <h2 className="font-head text-sm sm:text-base font-bold truncate">
                {editMode === "terrain"
                  ? t("rewards.sculpte_ton_terrain")
                  : editMode === "decor"
                    ? pending
                      ? movingUid
                        ? t("rewards.deplacer_nom", { name: pending.name })
                        : t("rewards.pose_nom", { name: pending.name })
                      : t("rewards.mode_editeur")
                    : t("rewards.ton_village")}
              </h2>
            </div>
            <div className="flex gap-1.5 items-center">
              <button
                type="button"
                onClick={toggleEdit}
                data-tour="edit-btn"
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${
                  editMode === "decor"
                    ? "bg-emerald-500 text-white"
                    : "border hover:bg-muted"
                }`}
              >
                <Pencil size={13} />{" "}
                {editMode ? t("quiz.terminer") : t("rewards.amenager")}
              </button>
              {editMode && (
                <button
                  type="button"
                  onClick={exitEditor}
                  className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold hover:bg-muted transition"
                >
                  <Check size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="h-[36vh] sm:h-[55vh] min-h-[220px] max-h-[500px] w-full relative">
            <IsoIsland
              key={imgVersion}
              island={island}
              editMode={editMode}
              pending={pending}
              pendingFlip={pendingFlip}
              paintGround={paintGround}
              balance={balance}
              chunkCost={nextChunkCost}
              movingUid={movingUid}
              onPlace={placeAt}
              onSelectPlaced={(id, pos) => {
                setSelectedPlaced(id);
                setMenuAt(id ? pos : null);
              }}
              onBuyChunk={buyChunk}
              onPaint={paintAt}
              selectedPlaced={selectedPlaced}
              calibrate={calibrate}
              adminSky={admin ? adminSky : { phase: null, rain: null }}
              onDropItem={dropItem}
              onToggleEdit={toggleEdit}
              onExitEditor={exitEditor}
              onFlipPending={() => setPendingFlip((f) => !f)}
              onFullscreenChange={setFullscreen}
              overlay={overlay}
              onCancelAction={() => {
                setPending(null);
                setPendingFlip(false);
                setMovingUid(null);
                setSelectedPlaced(null);
                setMenuAt(null);
              }}
            />
          </div>

          {editMode && !fullscreen && (
            <div className="px-3 pt-2 pb-1 flex gap-1.5 border-t">
              {[
                ["decor", t("rewards.objets"), Pencil],
                ["terrain", t("rewards.sol"), Brush],
              ].map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setEditMode(id);
                    setPending(null);
                    setSelectedPlaced(null);
                    setMenuAt(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-semibold transition ${
                    editMode === id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/70"
                  }`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t("rewards.confirmer_reset_ile")))
                    resetIsland();
                }}
                title={t("rewards.redemarrer_l_ile")}
                className="shrink-0 flex items-center justify-center rounded-xl px-2.5 py-1.5 text-xs font-semibold bg-muted hover:bg-destructive/10 hover:text-destructive transition"
              >
                <RotateCcw size={13} />
              </button>
            </div>
          )}

          {editMode === "terrain" && !fullscreen && (
            <div className="p-3">{terrainPalette}</div>
          )}

          {editMode === "decor" && selectedItem && menuAt && (
            <div
              className="fixed z-[60]"
              style={{
                left: Math.min(menuAt.x - 70, window.innerWidth - 170),
                top: menuAt.y + 14,
              }}
            >
              <div
                className="rounded-2xl border bg-card shadow-2xl overflow-hidden w-[160px]"
                style={{ animation: "oasisPop .18s ease-out" }}
              >
                <div className="px-3 py-2 border-b flex items-center gap-2">
                  <Sprite item={selectedItem} size={26} />
                  <span className="text-xs font-semibold truncate">
                    {selectedItem.name}
                  </span>
                </div>
                {selectedItem.layer !== "pnj" &&
                  selectedItem.layer !== "animal" && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          startMoving(selected);
                          setMenuAt(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition"
                      >
                        <Move size={13} /> {t("rewards.deplacer")}
                      </button>
                      <button
                        type="button"
                        onClick={() => flipPlaced(selected.uid)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition"
                      >
                        <FlipHorizontal2 size={13} />{" "}
                        {t("rewards.pivoter_miroir")}
                      </button>
                    </>
                  )}
                <button
                  type="button"
                  onClick={() => {
                    removePlaced(selected.uid);
                    setMenuAt(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-destructive hover:bg-destructive/10 transition"
                >
                  <Trash2 size={13} /> {t("rewards.remettre_au_sac")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlaced(null);
                    setMenuAt(null);
                  }}
                  className="w-full px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted transition"
                >
                  {t("groupui.annuler")}
                </button>
              </div>
              <style>{`@keyframes oasisPop{from{transform:scale(.9);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
            </div>
          )}

          {editMode === "decor" && !selectedItem && !fullscreen && (
            <div className="p-3 border-t">
              {pending && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-muted-foreground truncate">
                    {t("rewards.touche_une_case_libre_pour_poser")}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setPendingFlip((f) => !f)}
                      className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition ${
                        pendingFlip
                          ? "bg-sky-500 text-white"
                          : "border hover:bg-muted"
                      }`}
                    >
                      <FlipHorizontal2 size={12} /> {t("rewards.miroir")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPending(null);
                        setPendingFlip(false);
                        setMovingUid(null);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t("groupui.annuler")}
                    </button>
                  </div>
                </div>
              )}
              {ownedList.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("rewards.ton_sac_est_vide")}
                </p>
              ) : (
                decorPalette
              )}
            </div>
          )}

          {!editMode && (
            <div className="px-3 py-2 border-t text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <TrendingUp size={14} className="text-primary shrink-0" />
              {nextChunkCost === null ? (
                t("rewards.archipel_complet")
              ) : (
                <>
                  {t("rewards.prochaine_parcelle")}{" "}
                  <strong className="text-foreground">
                    {fmt(nextChunkCost)} {t("dinarcelebration.dinars")}
                  </strong>
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ONGLETS BOUTIQUE / COFFRES / SAC */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 space-y-3 mt-3">
        <div className="flex gap-1 overflow-x-auto rounded-xl border bg-card p-1">
          {TABS.map(({ id, label, icon: Icon, badge }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setTab(id);
                setPending(null);
              }}
              data-tour={`tab-${id}`}
              className={`flex-1 min-w-[70px] flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-semibold transition ${
                tab === id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <Icon size={15} /> {label}
              {badge ? (
                <span className="text-[10px] opacity-70">({badge})</span>
              ) : null}
            </button>
          ))}
        </div>

        {tab === "boutique" && (
          <Shop
            balance={balance}
            inventory={inventory}
            onBuy={buyItem}
            onBuyNpc={buyNpc}
            version={catalogVersion}
          />
        )}

        {tab === "coffres" && (
          <section className="space-y-3">
            <div className="rounded-2xl border bg-card p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <h2 className="font-head text-base sm:text-lg font-bold">
                  {t("rewards.coffres_du_souk")}
                </h2>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {pity} / {PITY_THRESHOLD} {t("rewards.garanti")}
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-amber-400 transition-all"
                  style={{
                    width: `${Math.min(100, (pity / PITY_THRESHOLD) * 100)}%`,
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {BOXES.map((box) => (
                <div
                  key={box.id}
                  className="rounded-2xl border bg-card overflow-hidden flex flex-col"
                >
                  <div
                    className={`bg-gradient-to-br ${box.accent} p-3 text-white`}
                  >
                    <Gift size={24} className="mb-1 opacity-90" />
                    <div className="font-head text-base font-bold leading-tight">
                      {box.name}
                    </div>
                    <div className="text-[11px] text-white/85">{box.tag}</div>
                  </div>
                  <div className="p-3 space-y-1 flex-1 text-xs">
                    {RARITY_ORDER.filter((r) => (box.odds[r] || 0) > 0).map(
                      (r) => (
                        <div
                          key={r}
                          className="flex items-center justify-between"
                        >
                          <span className="flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ background: RARITIES[r].color }}
                            />
                            {RARITIES[r].label}
                          </span>
                          <span className="tabular-nums text-muted-foreground">
                            {(box.odds[r] * 100).toFixed(
                              box.odds[r] < 0.01 ? 1 : 0,
                            )}{" "}
                            %
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openBox(box)}
                    disabled={balance < box.price}
                    className="m-3 mt-0 rounded-xl bg-primary text-primary-foreground font-bold py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition"
                  >
                    {t("rewards.ouvrir")} {fmt(box.price)} 🪙
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "sac" && (
          <section className="rounded-2xl border bg-card p-3 sm:p-4">
            <h2 className="font-head text-base sm:text-lg font-bold mb-2">
              {t("rewards.ton_sac")}
            </h2>
            {ownedList.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("rewards.rien_pour_l_instant")}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {ownedList.map((item) => {
                  const free = available(item.id);
                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border p-2 flex gap-2.5 items-center"
                    >
                      <Sprite item={item} size={40} className="shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-xs leading-tight truncate">
                          {item.name}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1">
                          <RarityChip rarity={item.rarity} />
                          <span className="text-[10px] text-muted-foreground">
                            ×{inventory[item.id]}
                          </span>
                        </div>
                        <div className="mt-1 flex gap-1">
                          <button
                            type="button"
                            disabled={free <= 0}
                            onClick={() => startPlacing(item)}
                            className="text-[10px] font-semibold rounded-full bg-primary text-primary-foreground px-2 py-0.5 disabled:opacity-40"
                          >
                            {t("rewards.poser")}
                          </button>
                          <button
                            type="button"
                            disabled={free <= 0}
                            onClick={() => sellItem(item)}
                            className="text-[10px] rounded-full border px-2 py-0.5 disabled:opacity-40 hover:bg-muted"
                          >
                            +{fmt(resaleValue(item))} 🪙
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      <ConfirmPurchase
        open={!!confirm}
        title={confirm?.title}
        subtitle={confirm?.subtitle}
        price={confirm?.price ?? 0}
        balance={balance}
        cta={confirm?.cta}
        allowQuantity={!!confirm?.allowQuantity}
        totalFor={confirm?.totalFor}
        preview={
          confirm?.item ? <Sprite item={confirm.item} size={72} /> : null
        }
        onCancel={() => setConfirm(null)}
        onConfirm={(qty) => {
          const c = confirm;
          setConfirm(null);
          c?.run?.(qty);
        }}
      />

      {opening && (
        <BoxOpening
          box={opening.box}
          result={opening.result}
          SpriteComp={<Sprite item={opening.result.item} size={104} />}
          onDone={(place) => {
            const it = opening.result.item;
            setOpening(null);
            if (place) startPlacing(it);
          }}
        />
      )}

      {celebrate && <ChunkCelebration onDone={() => setCelebrate(false)} />}
      {setReward && (
        <SetReward
          set={setReward}
          onClose={() => setSetReward(null)}
          onPlace={() => {
            const s2 = setReward;
            setSetReward(null);
            startPlacing(BY_ID[s2.npc.id]);
          }}
        />
      )}
      {admin && (
        <AdminPanel
          balance={balance}
          setBalance={setBalance}
          inventory={inventory}
          setInventory={setInventory}
          island={island}
          setIsland={setIsland}
          pity={pity}
          setPity={setPity}
          onGrantSet={grantSet}
          calibrate={calibrate}
          setCalibrate={setCalibrate}
          adminSky={adminSky}
          setAdminSky={setAdminSky}
          onResetIsland={resetIsland}
          onImagesChanged={() => {
            setCatalogVersion((v) => v + 1);
            setImgVersion((v) => v + 1);
          }}
          onOpenBox={(id) => {
            const box = BOXES.find((b) => b.id === id);
            if (box) openBox({ ...box, price: 0 });
          }}
          say={say}
        />
      )}
      {help && <HelpSheet onClose={() => setHelp(false)} />}
      <Tutorial active={tourActive} onClose={closeTour} />

      {shareOpen && (
        <div
          className="fixed inset-0 z-[85] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-card border p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 mb-1">
              <h3 className="font-head text-base font-bold flex items-center gap-2">
                <Share2 size={16} /> {t("rewards.partager_ton_ile")}
              </h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="rounded-full p-1 hover:bg-muted"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t("rewards.n_importe_qui_avec_ce_lien")}
            </p>
            {shareLink ? (
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareLink}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 rounded-xl border bg-muted px-2.5 py-1.5 text-xs"
                />
                <button
                  type="button"
                  onClick={copyShare}
                  className="shrink-0 rounded-xl bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold flex items-center gap-1 hover:brightness-110 transition"
                >
                  {copied ? (
                    <>
                      <CheckIcon size={12} /> {t("rewards.copie")}
                    </>
                  ) : (
                    t("rewards.copier")
                  )}
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground animate-pulse">
                {t("rewards.generation_du_lien")}
              </p>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium shadow-xl max-w-[92vw] text-center">
          {toast}
        </div>
      )}
    </div>
  );
}

function DecorPalette({
  ownedList,
  available,
  placedCount,
  pending,
  onPick,
  onDragStartItem,
  onDragEndItem,
}) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const item of ownedList) {
      const cat = item.cat || "objet";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(item);
    }
    const ordered = [];
    for (const c of CATEGORIES) {
      if (c.id === "all") continue;
      if (map.has(c.id)) {
        ordered.push([c.id, map.get(c.id)]);
        map.delete(c.id);
      }
    }
    for (const [id, list] of map) ordered.push([id, list]);
    return ordered;
  }, [ownedList]);

  const [cat, setCat] = useState("all");
  const visible =
    cat === "all" ? ownedList : groups.find(([id]) => id === cat)?.[1] || [];

  if (!ownedList.length)
    return (
      <p className="text-xs text-muted-foreground">
        {t("rewards.ton_sac_est_vide")}
      </p>
    );

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => setCat("all")}
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition ${
            cat === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-muted hover:bg-muted/70"
          }`}
        >
          {t("rewards.tout")}{" "}
          <span className="opacity-60">{ownedList.length}</span>
        </button>
        {groups.map(([id, list]) => (
          <button
            key={id}
            type="button"
            onClick={() => setCat(id)}
            className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition ${
              cat === id
                ? "bg-primary text-primary-foreground"
                : "bg-muted hover:bg-muted/70"
            }`}
          >
            {categoryLabel(id)}{" "}
            <span className="opacity-60">{list.length}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
        {visible.map((item) => {
          const libre = available(item.id);
          const pose = placedCount[item.id] || 0;
          return (
            <button
              key={item.id}
              type="button"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/oasis-item", item.id);
                e.dataTransfer.effectAllowed = "copy";
                onDragStartItem?.(item);
              }}
              onDragEnd={() => onDragEndItem?.()}
              onClick={() => onPick(item)}
              className={`relative rounded-xl border-2 p-1.5 transition ${
                pending?.id === item.id
                  ? "border-primary bg-primary/10"
                  : "border-transparent bg-muted hover:bg-muted/70"
              }`}
            >
              <div className={libre > 0 ? "" : "grayscale opacity-45"}>
                <Sprite item={item} size={36} className="mx-auto" />
              </div>
              <div className="text-[9px] leading-tight mt-1 line-clamp-2">
                {item.name}
              </div>
              {libre > 0 && (
                <span className="absolute top-0.5 right-0.5 text-[9px] font-bold bg-background rounded-full px-1">
                  {libre}
                </span>
              )}
              {pose > 0 && (
                <span
                  className="absolute top-0.5 left-0.5 text-[9px] font-bold bg-sky-500 text-white rounded-full px-1"
                  title={t("rewards.deja_pose")}
                >
                  {pose}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TerrainPalette({ ownedList, available, paintGround, onPick }) {
  const solItems = ownedList.filter((i) => i.layer === "sol");
  const Card = ({ active, onClick, children, badge, disabled }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`shrink-0 w-[72px] sm:w-[84px] rounded-xl border-2 p-1.5 transition relative ${
        active
          ? "border-sky-500 bg-sky-500/10"
          : "border-transparent bg-muted hover:bg-muted/70"
      } ${disabled ? "opacity-40" : ""}`}
    >
      {children}
      {badge != null && (
        <span className="absolute top-1 right-1 text-[9px] font-bold bg-background rounded-full px-1">
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-2">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          {t("rewards.textures")}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {GROUND_TYPES.map((g) => (
            <Card
              key={g.id}
              active={paintGround === g.id}
              onClick={() => onPick(g.id)}
            >
              <Sprite
                item={{
                  kind: "tile",
                  id: `ground_${g.id}`,
                  tile: g.tile,
                  name: g.label,
                }}
                size={38}
                className="mx-auto"
              />
              <div className="text-[9px] leading-tight mt-1">{g.label}</div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          {t("rewards.chemins_de_ton_sac")}
        </div>
        {solItems.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            {t("rewards.aucun_chemin_dans_ton_sac")}
          </p>
        ) : (
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {solItems.map((item) => {
              const libre = available(item.id);
              return (
                <Card
                  key={item.id}
                  active={paintGround === `item:${item.id}`}
                  onClick={() => onPick(`item:${item.id}`)}
                  badge={libre}
                  disabled={libre <= 0}
                >
                  <Sprite item={item} size={38} className="mx-auto" />
                  <div className="text-[9px] leading-tight mt-1 line-clamp-1">
                    {item.name}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Shop({ balance, inventory, onBuy, onBuyNpc, version }) {
  const cats = useMemo(() => {
    const out = [];
    for (const c of CATEGORIES) {
      if (c.id === "all" || c.id === "pnj") continue;
      const items = CATALOG.filter(
        (i) => i.cat === c.id && !i.set && !isNpc(i),
      );
      if (items.length) out.push({ id: c.id, label: c.label, items });
    }
    return out;
  }, [version]);

  const [tab, setTab] = useState("confrerie");
  const [openSetId, setOpenSetId] = useState(null);

  const active = ["confrerie", "habitants"].includes(tab)
    ? null
    : cats.find((c) => c.id === tab) || cats[0];

  const unlockedNpcs = useMemo(
    () =>
      SETS.filter(
        (set) =>
          set.buildings.length > 0 &&
          set.buildings.every((b) => (inventory[b.id] || 0) > 0),
      ),
    [inventory, version],
  );

  const Row = ({ item, showLock }) => {
    const price = priceOf(item);
    const has = inventory[item.id] || 0;
    if (showLock && !isShopItem(item)) {
      return (
        <div className="flex items-center gap-2 rounded-xl border border-dashed px-2 py-1.5 bg-card">
          <div className={has ? "" : "opacity-50"}>
            <Sprite item={item} size={28} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold truncate">{item.name}</div>
            <div className="text-[9px] text-muted-foreground flex items-center gap-1">
              <Lock size={9} /> {t("rewards.coffres")}
            </div>
          </div>
          {has ? (
            <Check size={13} className="text-emerald-600 shrink-0" />
          ) : null}
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={() => onBuy(item)}
        disabled={balance < price}
        className={`w-full flex items-center gap-2 rounded-xl border px-2 py-1.5 text-left transition bg-card
          ${balance < price ? "opacity-45" : "hover:bg-muted"} ${has ? "border-emerald-500/50 bg-emerald-500/5" : ""}`}
      >
        <Sprite item={item} size={30} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold leading-tight truncate">
            {item.name}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <RarityChip rarity={item.rarity} />
            {has ? (
              <span className="text-[9px] text-emerald-600 font-bold">
                ×{has}
              </span>
            ) : null}
          </div>
        </div>
        <span className="shrink-0 text-xs font-bold tabular-nums text-amber-600">
          {fmt(price)} 🪙
        </span>
      </button>
    );
  };

  return (
    <section className="flex flex-row items-start gap-2 sm:gap-3">
      <div className="flex flex-col gap-1 w-[80px] sm:w-[130px] shrink-0 sticky top-2">
        <button
          type="button"
          onClick={() => setTab("confrerie")}
          className={`flex flex-col items-center justify-center p-1.5 sm:p-2 rounded-xl border-2 transition text-center ${
            tab === "confrerie"
              ? "border-primary bg-primary/10 text-primary"
              : "border-transparent bg-muted hover:bg-muted/70 text-muted-foreground"
          }`}
        >
          <span className="text-[11px] sm:text-xs font-bold leading-tight">
            {t("rewards.confreries")}
          </span>
        </button>

        {cats.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setTab(c.id)}
            className={`flex flex-col items-center justify-center p-1.5 sm:p-2 rounded-xl border-2 transition text-center ${
              tab === c.id
                ? "border-primary bg-primary/10 text-primary"
                : "border-transparent bg-muted hover:bg-muted/70 text-muted-foreground"
            }`}
          >
            <span className="text-[11px] sm:text-xs font-bold leading-tight truncate w-full">
              {c.label}
            </span>
          </button>
        ))}

        <button
          type="button"
          onClick={() => setTab("habitants")}
          className={`flex flex-col items-center justify-center p-1.5 sm:p-2 rounded-xl border-2 transition text-center relative ${
            tab === "habitants"
              ? "border-primary bg-primary/10 text-primary"
              : "border-transparent bg-muted hover:bg-muted/70 text-muted-foreground"
          }`}
        >
          <span className="text-[11px] sm:text-xs font-bold leading-tight">
            {t("rewards.habitants")}
          </span>
          {unlockedNpcs.length > 0 && (
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground shadow-sm">
              {unlockedNpcs.length}
            </span>
          )}
        </button>
      </div>

      <div className="flex-1 min-w-0">
        {tab === "habitants" ? (
          <div className="space-y-1.5">
            {unlockedNpcs.length === 0 ? (
              <div className="rounded-xl border border-dashed bg-card p-4 text-center">
                <Lock
                  size={16}
                  className="mx-auto mb-1.5 text-muted-foreground"
                />
                <p className="text-xs text-muted-foreground">
                  {t("rewards.aucun_habitant_debloque_reunis_les_batiments")}
                </p>
              </div>
            ) : (
              <div className="grid gap-1.5 grid-cols-1 md:grid-cols-2">
                {unlockedNpcs.map((set) => {
                  const npcItem = BY_ID[set.npc.id];
                  if (!npcItem) return null;
                  const owned = inventory[set.npc.id] || 1;
                  const cost = npcPriceFor(owned);
                  return (
                    <button
                      key={set.id}
                      type="button"
                      onClick={() => onBuyNpc?.(npcItem)}
                      disabled={balance < cost}
                      className={`w-full flex items-center gap-2 rounded-xl border bg-card px-2 py-1.5 text-left transition ${balance < cost ? "opacity-45" : "hover:bg-muted"}`}
                    >
                      <Sprite item={npcItem} size={30} className="shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold leading-tight truncate">
                          {set.npc.name}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {set.name} {t("rewards.deja")}
                          {owned}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs font-bold tabular-nums text-amber-600">
                        {fmt(cost)} 🪙
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : tab === "confrerie" ? (
          <div className="space-y-2">
            {SETS.map((set) => {
              const owned = set.buildings.filter(
                (b) => (inventory[b.id] || 0) > 0,
              ).length;
              const done =
                set.buildings.length > 0 && owned === set.buildings.length;
              const npcItem = BY_ID[set.npc.id];
              const isOpen = openSetId === set.id;

              return (
                <div
                  key={set.id}
                  className="rounded-xl border bg-card overflow-hidden transition"
                >
                  <button
                    type="button"
                    onClick={() => setOpenSetId(isOpen ? null : set.id)}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-2 text-left transition hover:brightness-95"
                    style={{ background: `${set.color}18` }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={done ? "" : "grayscale opacity-55"}>
                        <Sprite item={npcItem} size={28} />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-xs font-bold leading-tight"
                          style={{ color: set.color }}
                        >
                          {set.npc.name}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {done
                            ? t("rewards.a_emmenage")
                            : `${owned}/${set.buildings.length} bâtiments`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-bold tabular-nums">
                        {owned}/{set.buildings.length}
                      </span>
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="p-2 grid gap-1.5 grid-cols-1 md:grid-cols-2 border-t">
                      {set.buildings.map((b) => {
                        const item = BY_ID[b.id];
                        return item ? (
                          <Row key={b.id} item={item} showLock />
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid gap-1.5 grid-cols-1 md:grid-cols-2">
            {(active?.items || []).map((i) =>
              isShopItem(i) ? (
                <Row key={i.id} item={i} />
              ) : (
                <Row key={i.id} item={i} showLock />
              ),
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function SetReward({ set, onClose, onPlace }) {
  const npcItem = BY_ID[set.npc.id];
  return (
    <div
      className="fixed inset-0 z-[85] grid place-items-center bg-black/65 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-card border p-5 text-center relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "oasisPop .4s cubic-bezier(.2,1.3,.4,1)" }}
      >
        <div
          className="absolute inset-x-0 -top-24 h-48 blur-2xl"
          style={{
            background: `radial-gradient(circle, ${set.color}88, transparent 70%)`,
          }}
        />
        <div className="relative">
          <div
            className="text-[11px] uppercase tracking-wider font-bold"
            style={{ color: set.color }}
          >
            {t("rewards.confrerie_complete")}
          </div>
          <h3 className="font-head text-xl font-extrabold mt-0.5">
            {set.name}
          </h3>
          <div
            className="mx-auto my-4 w-24 h-24 grid place-items-center rounded-3xl ring-4 bg-background"
            style={{
              borderColor: set.color,
              boxShadow: `0 0 0 4px ${set.color}55`,
            }}
          >
            <Sprite item={npcItem} size={80} />
          </div>
          <div className="font-head text-lg font-bold">{set.npc.name}</div>
          <p className="text-xs text-muted-foreground mt-1">{set.npc.desc}</p>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl border py-2 text-xs font-semibold hover:bg-muted transition"
            >
              {t("rewards.plus_tard")}
            </button>
            <button
              type="button"
              onClick={onPlace}
              className="flex-1 rounded-xl bg-primary text-primary-foreground py-2 text-xs font-semibold hover:brightness-110 transition"
            >
              {t("rewards.le_poser")}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes oasisPop { from { transform: scale(.85); opacity: 0 } to { transform: scale(1); opacity: 1 } }`}</style>
    </div>
  );
}

function HelpSheet({ onClose }) {
  const steps = [
    [t("rewards.aide_gagne_titre"), t("rewards.aide_gagne_desc")],
    [t("rewards.aide_agrandis_titre"), t("rewards.aide_agrandis_desc")],
    [t("rewards.aide_decore_titre"), t("rewards.aide_decore_desc")],
    [t("rewards.aide_recrute_titre"), t("rewards.aide_recrute_desc")],
    [t("rewards.aide_arrange_titre"), t("rewards.aide_arrange_desc")],
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-card border p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-head text-xl font-extrabold">
            {t("rewards.comment_ca_marche")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-muted"
          >
            <X size={18} />
          </button>
        </div>

        <div>
          <h3 className="font-semibold text-sm mb-2">
            {t("rewards.comment_tu_gagnes_des_dinars")}
          </h3>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              [t("rewards.gain_bonne_reponse"), `+${EARN.QUESTION_CLASSIQUE}`],
              [t("rewards.gain_lecon_terminee"), `+${EARN.LECON_VALIDEE}`],
              [t("rewards.gain_daily_plan"), `+${EARN.DAILY_PLAN_COMPLETED}`],
              [
                t("rewards.gain_mini_jeu_erreurs"),
                `×${EARN.MULT_MINI_JEU_ERREURS}`,
              ],
              [
                t("rewards.gain_mini_jeu_revision"),
                `×${EARN.MULT_MINI_JEU_REVISION}`,
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between rounded-xl bg-muted px-2.5 py-1.5 text-xs"
              >
                <span>{label}</span>
                <strong className="text-amber-600 dark:text-amber-400 tabular-nums font-bold">
                  {value}
                </strong>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2 border-t pt-3">
          {steps.map(([title, body], i) => (
            <div key={title} className="flex gap-2.5 text-xs">
              <span className="shrink-0 w-5 h-5 grid place-items-center rounded-full bg-primary/10 text-primary font-bold">
                {i + 1}
              </span>
              <div>
                <span className="font-semibold">{title} : </span>
                <span className="text-muted-foreground">{body}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-3">
          <h3 className="font-semibold text-xs mb-1.5">
            {t("rewards.les_cinq_raretes")}
          </h3>
          <div className="space-y-1">
            {RARITY_ORDER.map((id) => {
              const r = RARITIES[id];
              return (
                <div
                  key={id}
                  className="flex items-center justify-between rounded-lg bg-muted px-2.5 py-1 text-xs"
                >
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: r.color }}
                    />
                    {r.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {r.boxOnly
                      ? t("rewards.coffres_uniquement")
                      : t("rewards.boutique_prix", { price: fmt(r.price) })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t pt-3">
          <h3 className="font-semibold text-xs mb-1">
            {t("rewards.l_archipel_parcelle_par_parcelle")}
          </h3>
          <p className="text-[11px] text-muted-foreground mb-2">
            {t("rewards.33_parcelles_a_debloquer_apres_les")}
          </p>
          <div className="flex gap-1 overflow-x-auto pb-1">
            {Array.from(
              { length: MAX_CHUNKS - STARTING_CHUNKS.length },
              (_, i) => {
                const n = STARTING_CHUNKS.length + i;
                const tier = CHUNK_TIERS.find((item) => n + 1 === item.at);
                return (
                  <div
                    key={n}
                    className={`shrink-0 rounded-lg px-2 py-1 text-center text-xs ${tier ? "bg-primary/15 ring-1 ring-primary/40" : "bg-muted"}`}
                  >
                    <div className="text-[9px] text-muted-foreground">
                      {tier ? tier.label : `n°${n + 1}`}
                    </div>
                    <div className="font-bold tabular-nums">
                      {fmt(chunkPrice(n))}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
